import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Cpu, ShieldCheck, Activity, ListChecks, AlertTriangle, Plus, CheckCircle2, GitBranch, Bot } from "lucide-react";
import { toast } from "sonner";

export default function AIMLGovernance() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");

  useEffect(() => {
    if (authLoading) return; // wait for AuthContext to finish so the bearer header is attached
    api.get("/devices").then(({ data }) => {
      setProjects(data);
      if (data[0]) setPid(data[0].project_id);
    }).catch(() => {});
  }, [authLoading]);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2">
            <Cpu className="w-3 h-3" /> AI / ML Governance
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            PCCP, Model Registry & Real-World Monitoring
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            FDA PCCP (Dec 2024), GMLP-10 (2021), and EU AI Act (2024) aligned workflows.
            Every artefact requires qualified human review — no autonomous approval.
          </p>
        </div>

        <div className="mb-6 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="aiml-project-select" className="mt-1">
              <SelectValue placeholder="Select project…" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.project_id} value={p.project_id}>
                  {p.device_name || p.project_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {pid && (
          <Tabs defaultValue="pccp">
            <TabsList data-testid="aiml-tabs">
              <TabsTrigger value="pccp" data-testid="tab-pccp"><ShieldCheck className="w-4 h-4 mr-1" /> PCCP</TabsTrigger>
              <TabsTrigger value="models" data-testid="tab-models"><Cpu className="w-4 h-4 mr-1" /> Model registry</TabsTrigger>
              <TabsTrigger value="monitor" data-testid="tab-monitor"><Activity className="w-4 h-4 mr-1" /> Real-world monitoring</TabsTrigger>
              <TabsTrigger value="gmlp" data-testid="tab-gmlp"><ListChecks className="w-4 h-4 mr-1" /> GMLP-10</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="tab-timeline"><GitBranch className="w-4 h-4 mr-1" /> Impact Timeline</TabsTrigger>
              <TabsTrigger value="gpai" data-testid="tab-gpai"><Bot className="w-4 h-4 mr-1" /> GPAI &amp; Red-Team</TabsTrigger>
            </TabsList>

            <TabsContent value="pccp"><PCCPTab pid={pid} /></TabsContent>
            <TabsContent value="models"><ModelRegistryTab pid={pid} /></TabsContent>
            <TabsContent value="monitor"><MonitoringTab pid={pid} /></TabsContent>
            <TabsContent value="gmlp"><GMLPTab pid={pid} /></TabsContent>
            <TabsContent value="timeline"><TimelineTab pid={pid} /></TabsContent>
            <TabsContent value="gpai"><GPAITab pid={pid} /></TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}

function PCCPTab({ pid }) {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ title: "", sps: "", protocol: "", impact: "" });
  const [creating, setCreating] = useState(false);

  const load = () => api.get(`/ai/pccp?project_id=${pid}`).then(({ data }) => setPlans(data));
  useEffect(() => { load(); }, [pid]);

  const create = async () => {
    const sps_modifications = form.sps.split("\n").map(s => s.trim()).filter(Boolean);
    if (!form.title || sps_modifications.length === 0 || form.protocol.length < 50 || form.impact.length < 50) {
      toast.error("Fill all fields. Protocol + impact must be at least 50 chars.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/ai/pccp", { project_id: pid, title: form.title, sps_modifications,
        modification_protocol: form.protocol, impact_assessment: form.impact });
      toast.success("PCCP draft created");
      setForm({ title: "", sps: "", protocol: "", impact: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const approve = async (pccp_id) => {
    try { await api.post(`/ai/pccp/${pccp_id}/approve`); toast.success("PCCP approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="vq-card p-6">
        <h3 className="font-display font-bold text-lg mb-1">New PCCP (FDA Dec 2024)</h3>
        <p className="text-xs text-slate-500 mb-4">Three sections required: SPS (allowed modifications), Modification Protocol (V&V methodology), Impact Assessment (benefit-risk).</p>
        <div className="space-y-3">
          <Input placeholder="Title" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="pccp-title" />
          <Textarea placeholder="SPS — one modification per line. e.g. Quarterly retraining on new EEG data" rows={3} value={form.sps} onChange={e=>setForm({...form,sps:e.target.value})} data-testid="pccp-sps" />
          <Textarea placeholder="Modification protocol (V&V methodology, acceptance criteria, cybersecurity, rollout)" rows={4} value={form.protocol} onChange={e=>setForm({...form,protocol:e.target.value})} data-testid="pccp-protocol" />
          <Textarea placeholder="Impact assessment (benefit-risk of the collective modifications)" rows={3} value={form.impact} onChange={e=>setForm({...form,impact:e.target.value})} data-testid="pccp-impact" />
          <Button onClick={create} disabled={creating} className="bg-slate-900 hover:bg-[#0055FF] text-white" data-testid="pccp-create-btn">
            <Plus className="w-4 h-4 mr-1" /> Create draft
          </Button>
        </div>
      </div>

      <div className="vq-card p-6">
        <h3 className="font-display font-bold text-lg mb-4">Plans for this device</h3>
        {plans.length === 0 ? <div className="text-sm text-slate-500">No PCCPs yet.</div> : plans.map(p => (
          <div key={p.pccp_id} className="border-b border-slate-100 py-3 last:border-0" data-testid={`pccp-row-${p.pccp_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900">{p.title}</div>
                <div className="text-xs text-slate-500 font-mono">{p.pccp_id} · {p.sps_modifications.length} SPS · <span className={p.state==="approved"?"text-emerald-700":"text-amber-700"}>{p.state}</span></div>
              </div>
              {p.state === "draft" && <Button size="sm" onClick={() => approve(p.pccp_id)} data-testid={`pccp-approve-${p.pccp_id}`}>Approve</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModelRegistryTab({ pid }) {
  const [models, setModels] = useState([]);
  const [form, setForm] = useState({ name: "", version: "", data_ref: "", auc: "", sensitivity: "", specificity: "" });

  const load = () => api.get(`/ai/models?project_id=${pid}`).then(({ data }) => setModels(data));
  useEffect(() => { load(); }, [pid]);

  const register = async () => {
    if (!form.name || !form.version || !form.data_ref) { toast.error("Name, version, and training data reference are required."); return; }
    try {
      const metrics = {};
      if (form.auc) metrics.auc = parseFloat(form.auc);
      if (form.sensitivity) metrics.sensitivity = parseFloat(form.sensitivity);
      if (form.specificity) metrics.specificity = parseFloat(form.specificity);
      await api.post("/ai/models", { project_id: pid, model_name: form.name, version: form.version,
        training_data_ref: form.data_ref, evaluation_metrics: metrics });
      toast.success("Model version registered");
      setForm({ name: "", version: "", data_ref: "", auc: "", sensitivity: "", specificity: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="vq-card p-6">
        <h3 className="font-display font-bold text-lg mb-1">Register new model version</h3>
        <p className="text-xs text-slate-500 mb-4">Every deployed AI/ML model version gets a lineage row.</p>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Model name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="model-name" />
          <Input placeholder="Version (e.g. 1.2.0)" value={form.version} onChange={e=>setForm({...form,version:e.target.value})} data-testid="model-version" />
        </div>
        <Input className="mt-3" placeholder="Training data reference (s3:// path, URL, or DHF section)" value={form.data_ref} onChange={e=>setForm({...form,data_ref:e.target.value})} data-testid="model-data-ref" />
        <div className="grid grid-cols-3 gap-3 mt-3">
          <Input placeholder="AUC" type="number" step="0.01" value={form.auc} onChange={e=>setForm({...form,auc:e.target.value})} data-testid="model-auc" />
          <Input placeholder="Sensitivity" type="number" step="0.01" value={form.sensitivity} onChange={e=>setForm({...form,sensitivity:e.target.value})} />
          <Input placeholder="Specificity" type="number" step="0.01" value={form.specificity} onChange={e=>setForm({...form,specificity:e.target.value})} />
        </div>
        <Button onClick={register} className="mt-4 bg-slate-900 hover:bg-[#0055FF] text-white" data-testid="model-register-btn">
          <Plus className="w-4 h-4 mr-1" /> Register
        </Button>
      </div>

      <div className="vq-card p-6">
        <h3 className="font-display font-bold text-lg mb-4">Registered versions</h3>
        {models.length === 0 ? <div className="text-sm text-slate-500">No model versions yet.</div> : (
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr><th className="text-left py-2">Model</th><th className="text-left py-2">Version</th><th className="text-left py-2">Metrics</th><th className="text-left py-2">Registered</th></tr>
            </thead>
            <tbody>
              {models.map(m => (
                <tr key={m.model_version_id} className="border-b border-slate-100" data-testid={`model-row-${m.model_version_id}`}>
                  <td className="py-2 font-medium">{m.model_name}</td>
                  <td className="py-2 font-mono">{m.version}</td>
                  <td className="py-2 text-xs text-slate-600">{Object.entries(m.evaluation_metrics||{}).map(([k,v])=>`${k}=${v}`).join(" · ")}</td>
                  <td className="py-2 text-xs text-slate-500">{new Date(m.registered_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MonitoringTab({ pid }) {
  const [models, setModels] = useState([]);
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ metric: "auc", value: "", baseline: "", sample_size: "" });

  useEffect(() => { api.get(`/ai/models?project_id=${pid}`).then(({ data }) => { setModels(data); if (data[0]) setSelected(data[0].model_version_id); }); }, [pid]);
  const load = () => selected && api.get(`/ai/models/${selected}/metrics`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [selected]);

  const ingest = async () => {
    if (!selected || !form.value || !form.baseline) { toast.error("Value and baseline required."); return; }
    try {
      await api.post(`/ai/models/${selected}/metrics`, {
        model_version_id: selected, metric_name: form.metric,
        value: parseFloat(form.value), baseline: parseFloat(form.baseline),
        sample_size: form.sample_size ? parseInt(form.sample_size) : null,
      });
      toast.success("Metric recorded");
      setForm({ metric: "auc", value: "", baseline: "", sample_size: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-6 space-y-6">
      {models.length === 0 ? (
        <div className="vq-card p-6 text-sm text-slate-500">Register a model version first.</div>
      ) : (
        <>
          <div className="vq-card p-6">
            <h3 className="font-display font-bold text-lg mb-3">Ingest real-world metric</h3>
            <div className="grid grid-cols-4 gap-3">
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{models.map(m => <SelectItem key={m.model_version_id} value={m.model_version_id}>{m.model_name} v{m.version}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Metric name" value={form.metric} onChange={e=>setForm({...form,metric:e.target.value})} data-testid="perf-metric" />
              <Input placeholder="Observed value" type="number" step="0.01" value={form.value} onChange={e=>setForm({...form,value:e.target.value})} data-testid="perf-value" />
              <Input placeholder="Baseline" type="number" step="0.01" value={form.baseline} onChange={e=>setForm({...form,baseline:e.target.value})} data-testid="perf-baseline" />
            </div>
            <Button onClick={ingest} className="mt-3 bg-slate-900 hover:bg-[#0055FF] text-white" data-testid="perf-ingest-btn">Record metric</Button>
          </div>

          {data && (
            <div className="vq-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg">Metric history</h3>
                {data.active_drift_alerts > 0 && (
                  <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1" data-testid="drift-banner">
                    <AlertTriangle className="w-4 h-4" /> {data.active_drift_alerts} active drift alert(s) · threshold {data.threshold_pct}%
                  </div>
                )}
              </div>
              {data.metrics.length === 0 ? <div className="text-sm text-slate-500">No metrics recorded yet.</div> : (
                <table className="w-full text-sm">
                  <thead className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr><th className="text-left py-2">Metric</th><th className="text-left py-2">Value</th><th className="text-left py-2">Baseline</th><th className="text-left py-2">Drift</th><th className="text-left py-2">Recorded</th></tr>
                  </thead>
                  <tbody>
                    {data.metrics.map(m => (
                      <tr key={m.metric_id} className={`border-b border-slate-100 ${m.drift_alert ? "bg-amber-50" : ""}`}>
                        <td className="py-2 font-medium">{m.metric_name}</td>
                        <td className="py-2 font-mono">{m.value}</td>
                        <td className="py-2 font-mono text-slate-500">{m.baseline ?? "—"}</td>
                        <td className="py-2 font-mono">{m.drift_pct != null ? `${m.drift_pct}%` : "—"} {m.drift_alert && <AlertTriangle className="w-3 h-3 inline text-amber-600" />}</td>
                        <td className="py-2 text-xs text-slate-500">{new Date(m.recorded_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GMLPTab({ pid }) {
  const [checklist, setChecklist] = useState(null);
  const load = () => api.get(`/ai/gmlp/${pid}`).then(({ data }) => setChecklist(data));
  useEffect(() => { load(); }, [pid]);

  const update = async (principle_id, patch) => {
    try {
      await api.post(`/ai/gmlp/${pid}`, { principle_id, ...patch });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!checklist) return <div className="mt-6 vq-card p-6 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="mt-6 vq-card p-6">
      <h3 className="font-display font-bold text-lg mb-1">FDA Good Machine Learning Practice — 10 principles</h3>
      <p className="text-xs text-slate-500 mb-6">Document evidence for each principle. Every auditor working on AI/ML SaMD asks about GMLP.</p>
      <div className="space-y-3">
        {checklist.principles.map(p => (
          <div key={p.id} className="border border-slate-200 rounded p-3" data-testid={`gmlp-principle-${p.id}`}>
            <div className="flex items-start gap-3">
              <div className="text-xs font-mono font-bold text-slate-500 min-w-[24px]">{p.id}</div>
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900 mb-2">{p.principle}</div>
                <div className="grid grid-cols-3 gap-2">
                  <Select value={p.status} onValueChange={v => update(p.id, { status: v, evidence_link: p.evidence_link, notes: p.notes })}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`gmlp-status-${p.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not started</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="evidence_documented">Evidence documented</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input className="h-8 text-xs col-span-2" placeholder="Evidence link / DHF ref" defaultValue={p.evidence_link || ""} onBlur={e => e.target.value !== (p.evidence_link || "") && update(p.id, { status: p.status, evidence_link: e.target.value, notes: p.notes })} />
                </div>
              </div>
              {p.status === "evidence_documented" && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Regulatory Impact Timeline — the single-screenshot auditor view.
// Device → PCCP → Model → Monitoring → Drift → Risk, one horizontal stream.
// -----------------------------------------------------------------------------
const TIMELINE_CATEGORIES = {
  device:       { label: "Device",      color: "#0F172A", bg: "#E2E8F0" },
  pccp:         { label: "PCCP",        color: "#0055FF", bg: "#DBEAFE" },
  model:        { label: "Model",       color: "#7C3AED", bg: "#EDE9FE" },
  monitoring:   { label: "Monitoring",  color: "#059669", bg: "#D1FAE5" },
  drift_alert:  { label: "Drift alert", color: "#DC2626", bg: "#FEE2E2" },
  risk:         { label: "Risk",        color: "#B45309", bg: "#FEF3C7" },
};

function TimelineTab({ pid }) {
  const [data, setData] = useState({ events: [], counts: {}, total_events: 0 });
  const [filters, setFilters] = useState(() =>
    Object.fromEntries(Object.keys(TIMELINE_CATEGORIES).map((k) => [k, true]))
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/ai/timeline/${pid}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Timeline load failed."))
      .finally(() => setLoading(false));
  }, [pid]);

  const visibleEvents = (data.events || []).filter((e) => filters[e.category]);

  if (loading) return <div className="mt-6 text-sm text-slate-500">Loading timeline…</div>;

  return (
    <div className="mt-6 space-y-5" data-testid="ai-timeline-panel">
      <div className="vq-card p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="font-display font-bold text-slate-900">Regulatory Impact Timeline</div>
            <div className="text-xs text-slate-500 mt-1">
              {data.total_events} event{data.total_events === 1 ? "" : "s"} · {data.device_name}
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="timeline-legend">
            {Object.entries(TIMELINE_CATEGORIES).map(([k, meta]) => (
              <button
                key={k}
                data-testid={`legend-${k}`}
                onClick={() => setFilters({ ...filters, [k]: !filters[k] })}
                className={`text-xs px-2 py-1 rounded-full border transition ${
                  filters[k] ? "opacity-100" : "opacity-40"
                }`}
                style={{ borderColor: meta.color, color: meta.color, backgroundColor: meta.bg }}
              >
                {meta.label}
                <span className="ml-1 font-mono">{data.counts?.[k] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        {visibleEvents.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-10">
            No timeline events for this device. Register a PCCP, a model version, or ingest a monitoring metric to
            populate the timeline.
          </div>
        ) : (
          <div className="relative pl-8 border-l-2 border-slate-200" data-testid="timeline-vertical">
            {visibleEvents.map((e, i) => {
              const meta = TIMELINE_CATEGORIES[e.category] || TIMELINE_CATEGORIES.device;
              // Compose a stable key from the event's timestamp + category + title —
              // avoids array-index reordering issues if the parent filter reshuffles.
              const stableKey = `${e.ts || "no-ts"}-${e.category}-${e.title || i}`;
              return (
                <div key={stableKey} className="relative pb-6 last:pb-1" data-testid={`timeline-event-${i}`}>
                  <div
                    className="absolute -left-[41px] top-1 w-5 h-5 rounded-full border-4 border-white shadow"
                    style={{ backgroundColor: meta.color }}
                  />
                  <div
                    className="inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                    style={{ backgroundColor: meta.bg, color: meta.color }}
                  >
                    {meta.label}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {e.ts ? new Date(e.ts).toLocaleString() : "—"}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1">{e.title}</div>
                  {e.detail && <div className="text-xs text-slate-600 mt-1">{e.detail}</div>}
                  {(e.linked_pccp_id || e.linked_model_version_id) && (
                    <div className="text-[10px] font-mono text-slate-400 mt-1">
                      {e.linked_pccp_id && <>PCCP <span className="text-slate-600">{e.linked_pccp_id}</span> · </>}
                      {e.linked_model_version_id && <>Model <span className="text-slate-600">{e.linked_model_version_id}</span></>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="vq-card p-3 border-l-4 border-l-slate-400 bg-slate-50 text-xs text-slate-600 flex items-start gap-2">
        <GitBranch className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          Timeline aggregates the lifecycle-of-evidence for this AI/ML device: <b>device creation → PCCP →
          model versions → real-world monitoring → drift alerts → linked Risk register updates</b>. Ideal for
          auditor walkthroughs. Underlying records live in their respective tabs and modules.
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// GPAI harm assessment + Red-team session log.
// Aligned with NIST AI RMF GPAI Profile, EU AI Act, ISO/IEC 42001, ISO/IEC 23894.
// -----------------------------------------------------------------------------
function GPAITab({ pid }) {
  const [assessment, setAssessment] = useState({ categories: [], total: 0 });
  const [redteams, setRedteams] = useState([]);
  const [editCat, setEditCat] = useState(null);
  const [catForm, setCatForm] = useState({ applicable: true, likelihood: 3, severity: 3, mitigations: "", evidence_link: "" });
  const [rtOpen, setRtOpen] = useState(false);
  const [rtForm, setRtForm] = useState({
    session_date: new Date().toISOString().slice(0, 10),
    scope: "", methodology: "adversarial_prompts",
    findings_summary: "", critical_findings: 0, high_findings: 0,
    remediation_plan: "",
  });

  const loadAll = () => {
    api.get(`/ai/gpai/assessment/${pid}`).then(({ data }) => setAssessment(data));
    api.get(`/ai/redteam?project_id=${pid}`).then(({ data }) => setRedteams(data));
  };
  useEffect(() => { loadAll(); }, [pid]);

  const openCat = (c) => {
    setEditCat(c);
    setCatForm({
      applicable: c.applicable ?? true,
      likelihood: c.likelihood || 3, severity: c.severity || 3,
      mitigations: c.mitigations || "", evidence_link: c.evidence_link || "",
    });
  };
  const saveCat = async () => {
    try {
      await api.post("/ai/gpai/assessment", {
        project_id: pid, harm_category: editCat.harm_category, ...catForm,
      });
      toast.success("Assessment saved.");
      setEditCat(null); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const submitRT = async () => {
    try {
      await api.post("/ai/redteam", { project_id: pid, ...rtForm });
      toast.success("Red-team session logged.");
      setRtOpen(false);
      setRtForm({ session_date: new Date().toISOString().slice(0, 10),
        scope: "", methodology: "adversarial_prompts",
        findings_summary: "", critical_findings: 0, high_findings: 0, remediation_plan: "" });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="vq-card p-4">
        <div className="font-display font-bold text-slate-900">GPAI harm-category assessment</div>
        <div className="text-xs text-slate-500 mt-1 mb-3">
          Scored via NIST AI RMF GPAI Profile. Use for foundation-model / LLM based SaMDs.
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-center px-3 py-2 w-16">L</th>
              <th className="text-center px-3 py-2 w-16">S</th>
              <th className="text-center px-3 py-2 w-16">Risk</th>
              <th className="text-right px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody data-testid="gpai-tbody">
            {assessment.categories.map((c) => (
              <tr key={c.harm_category} className={`border-t border-slate-100 ${!c.applicable ? "opacity-50" : ""}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{c.harm_category.replace(/_/g, " ")}</div>
                  <div className="text-xs text-slate-500">{c.description}</div>
                </td>
                <td className="px-3 py-2 text-center text-sm">{c.likelihood ?? "—"}</td>
                <td className="px-3 py-2 text-center text-sm">{c.severity ?? "—"}</td>
                <td className="px-3 py-2 text-center font-bold text-sm"
                    style={{ color: (c.risk_score || 0) >= 15 ? "#dc2626" : (c.risk_score || 0) >= 8 ? "#d97706" : "#059669" }}>
                  {c.risk_score ?? "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" data-testid={`gpai-edit-${c.harm_category}`} onClick={() => openCat(c)}>Score</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="vq-card p-4">
        <div className="flex justify-between items-center mb-3">
          <div>
            <div className="font-display font-bold text-slate-900">Red-team sessions</div>
            <div className="text-xs text-slate-500 mt-1">Adversarial testing evidence. Attach to model versions for lifecycle traceability.</div>
          </div>
          <Button data-testid="new-redteam-btn" size="sm" onClick={() => setRtOpen(true)}><Plus className="w-4 h-4 mr-1" /> Log session</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Methodology</th>
              <th className="text-left px-3 py-2">Scope</th>
              <th className="text-center px-3 py-2">Findings</th>
            </tr>
          </thead>
          <tbody data-testid="redteam-tbody">
            {redteams.length === 0 && <tr><td colSpan="4" className="text-center px-3 py-6 text-slate-400">No red-team sessions yet.</td></tr>}
            {redteams.map((r) => (
              <tr key={r.session_id} className="border-t border-slate-100">
                <td className="px-3 py-2 text-xs">{r.session_date}</td>
                <td className="px-3 py-2 text-xs">{r.methodology.replace(/_/g, " ")}</td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-md truncate">{r.scope}</td>
                <td className="px-3 py-2 text-center text-xs">
                  {r.critical_findings > 0 && <span className="text-red-600 font-bold mr-1">C:{r.critical_findings}</span>}
                  {r.high_findings > 0 && <span className="text-amber-600 font-bold">H:{r.high_findings}</span>}
                  {r.critical_findings === 0 && r.high_findings === 0 && <span className="text-emerald-600">Clean</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editCat} onOpenChange={(o) => !o && setEditCat(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assess: {editCat?.harm_category.replace(/_/g, " ")}</DialogTitle>
            <DialogDescription>{editCat?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input data-testid="gpai-applicable" type="checkbox" checked={catForm.applicable}
                     onChange={(e) => setCatForm({ ...catForm, applicable: e.target.checked })} />
              Applicable to this device
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Likelihood (1–5)</Label>
                <Input data-testid="gpai-likelihood" type="number" min={1} max={5}
                       value={catForm.likelihood}
                       onChange={(e) => setCatForm({ ...catForm, likelihood: parseInt(e.target.value) || 1 })} />
              </div>
              <div>
                <Label>Severity (1–5)</Label>
                <Input data-testid="gpai-severity" type="number" min={1} max={5}
                       value={catForm.severity}
                       onChange={(e) => setCatForm({ ...catForm, severity: parseInt(e.target.value) || 1 })} />
              </div>
            </div>
            <div>
              <Label>Mitigations</Label>
              <Textarea data-testid="gpai-mitigations" rows={3}
                        value={catForm.mitigations}
                        onChange={(e) => setCatForm({ ...catForm, mitigations: e.target.value })} />
            </div>
            <div>
              <Label>Evidence link</Label>
              <Input data-testid="gpai-evidence" value={catForm.evidence_link}
                     onChange={(e) => setCatForm({ ...catForm, evidence_link: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditCat(null)}>Cancel</Button>
            <Button data-testid="gpai-save" onClick={saveCat}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rtOpen} onOpenChange={setRtOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Log red-team session</DialogTitle>
            <DialogDescription>Adversarial testing evidence — attach to a model version for lifecycle traceability.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Session date</Label>
              <Input data-testid="rt-date" type="date" value={rtForm.session_date}
                     onChange={(e) => setRtForm({ ...rtForm, session_date: e.target.value })} />
            </div>
            <div>
              <Label>Methodology</Label>
              <Select value={rtForm.methodology} onValueChange={(v) => setRtForm({ ...rtForm, methodology: v })}>
                <SelectTrigger data-testid="rt-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="structured_probes">Structured probes</SelectItem>
                  <SelectItem value="adversarial_prompts">Adversarial prompts</SelectItem>
                  <SelectItem value="jailbreak">Jailbreak</SelectItem>
                  <SelectItem value="capability_evaluation">Capability evaluation</SelectItem>
                  <SelectItem value="socio_technical">Socio-technical</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Scope</Label>
              <Textarea data-testid="rt-scope" rows={2} value={rtForm.scope}
                        onChange={(e) => setRtForm({ ...rtForm, scope: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Findings summary</Label>
              <Textarea data-testid="rt-findings" rows={4} value={rtForm.findings_summary}
                        onChange={(e) => setRtForm({ ...rtForm, findings_summary: e.target.value })} />
            </div>
            <div>
              <Label>Critical findings</Label>
              <Input data-testid="rt-crit" type="number" min={0} value={rtForm.critical_findings}
                     onChange={(e) => setRtForm({ ...rtForm, critical_findings: parseInt(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>High findings</Label>
              <Input data-testid="rt-high" type="number" min={0} value={rtForm.high_findings}
                     onChange={(e) => setRtForm({ ...rtForm, high_findings: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="col-span-2">
              <Label>Remediation plan (optional)</Label>
              <Textarea data-testid="rt-remediation" rows={3} value={rtForm.remediation_plan}
                        onChange={(e) => setRtForm({ ...rtForm, remediation_plan: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRtOpen(false)}>Cancel</Button>
            <Button data-testid="rt-submit" onClick={submitRT}
                    disabled={rtForm.scope.length < 10 || rtForm.findings_summary.length < 10}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

